import { giphyAqua, giphyBlack, giphyLightGrey } from '@giphy/colors'
import { useContext, useEffect, useState } from 'react'
import { useKeyPress } from 'react-use'
import { fetchChannel } from 'shared/api/channels'
import CollectionsCabinet from 'shared/components/collections/collections-cabinet'
import { CollectionContext } from 'shared/contexts/add-to-collections-manager'
import UserContext from 'shared/contexts/user'
import { IChannel } from 'shared/types/channel'
import styled from 'styled-components'

const OPENED_CABINET_HEIGHT = 277
const CLOSED_CABINET_HEIGHT = 44

const Container = styled.div`
    margin-bottom: 20px;
`

const CabinetContainer = styled.div<{ isOpen?: boolean }>`
    display: flex;
    flex-direction: column;
    position: relative;
    height: ${({ isOpen }) => (isOpen ? OPENED_CABINET_HEIGHT : CLOSED_CABINET_HEIGHT)}px;
    padding-bottom: 10px;
    background: ${giphyBlack};
    padding-left: 10px;
    padding-right: 10px;
    overflow: hidden;
    border-radius: ${({ isOpen }) => !isOpen && `0px 4px 0px 0px`};
`

const HeaderTitle = styled.div<{ adding?: boolean }>`
    display: flex;
    align-items: center;
    font-family: 'interface';
    font-size: 15px;
    font-weight: ${(props) => (props.adding ? 'regular' : 'bold')};
    b {
        font-weight: bold;
    }
`

const ElipisisOverflow = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 348px;
`

const IconCheck = styled.span`
    background: ${giphyAqua};
    color: black;
    height: 20px;
    width: 20px;
    font-size: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    margin-right: 4px;

    &:before {
        margin-top: 2px;
    }
`

const Label = styled.label`
    color: ${giphyLightGrey};
    font-size: 14px;
    text-transform: capitalize;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-weight: 600;
    display: block;
    margin-bottom: 8px;
`
const getTitle = (channels: IChannel[]): string => {
    return channels.map((channel) => channel.display_name).join(', ')
}
type Props = {
    showLabel?: boolean
    onChange: (data: { channelId: string[] | null }) => void
    itemWidth?: number
}
const Channels = ({ showLabel = true, onChange, itemWidth }: Props) => {
    const { loadChannels, channels, collectionCreatedSuccessMessage, setActiveChannelId } =
        useContext(CollectionContext)
    const { user } = useContext(UserContext)
    const [selectedChannels, setSelectedChannels] = useState<IChannel[]>([])
    const [loadingCollections, setLoadingCollections] = useState<boolean>(true)
    const [isOpen, setIsOpen] = useState<boolean>(true)
    const [isEscPressed] = useKeyPress((event: KeyboardEvent) => event.keyCode === 27)
    const handleAdd = async (channelId: number) => {
        const channel = await fetchChannel(channelId)
        setCabinetSearchFocused(false)

        // Check if channel is already selected
        if (selectedChannels.find((channel) => channel.id === channelId)) {
            // If is already selected, unselect
            setSelectedChannels(selectedChannels.filter((channel) => channel.id !== channelId))
        } else {
            // If not, select
            setSelectedChannels([...selectedChannels, channel])
        }

        setActiveChannelId(user.channelId)
    }
    useEffect(() => {
        onChange({
            channelId: selectedChannels.map((channel) => `${channel.id}`),
        })
    }, [selectedChannels])
    const handleClickChannel = (channelId: number) => {
        if (channels[channelId]?.has_children) {
            setActiveChannelId(channelId)
        } else {
            handleAdd(channelId)
        }
    }
    const [isCabinetSearchFocused, setCabinetSearchFocused] = useState<boolean>()
    useEffect(() => {
        if (isEscPressed && isCabinetSearchFocused) {
            setCabinetSearchFocused(false)
        }
    }, [isEscPressed, setSelectedChannels, isCabinetSearchFocused])

    useEffect(() => {
        if (collectionCreatedSuccessMessage?.channel.id) {
            handleAdd(collectionCreatedSuccessMessage.channel.id)
        }
    }, [collectionCreatedSuccessMessage])

    const loadCollections = async () => {
        setLoadingCollections(true)
        await loadChannels()
        setLoadingCollections(false)
    }

    useEffect(() => {
        loadCollections()
    }, [])

    if (loadingCollections) {
        return <></>
    }

    return (
        <Container>
            {showLabel && <Label>Collections</Label>}
            <CabinetContainer isOpen={isOpen}>
                <CollectionsCabinet
                    selectedChannelIds={selectedChannels.map((channel) => channel.id)}
                    onClickChannel={handleClickChannel}
                    onClickAddTo={handleAdd}
                    itemWidth={itemWidth}
                    headerTitle={
                        selectedChannels.length > 0 ? (
                            <HeaderTitle adding>
                                <IconCheck className="ss-check" />
                                <ElipisisOverflow>
                                    Adding to <b>{getTitle(selectedChannels)}</b>
                                </ElipisisOverflow>
                            </HeaderTitle>
                        ) : isOpen ? (
                            <HeaderTitle>Choose a Collection</HeaderTitle>
                        ) : (
                            <HeaderTitle>Add to Collection</HeaderTitle>
                        )
                    }
                    onClickCollapse={() => setIsOpen(!isOpen)}
                    isOpen={isOpen}
                    showGifsBelow={false}
                    isInCreationTool={true}
                />
            </CabinetContainer>
        </Container>
    )
}

export default Channels
